/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Flex,
  Group,
  HStack,
  IconButton,
  Input,
  InputAddon,
  Menu,
  Spinner,
  Stack
} from '@chakra-ui/react';
import {
  CaretDown,
  CheckCircle,
  File,
  PencilSimpleLine,
  Plus,
  Trash,
  UploadSimple,
  X
} from '@phosphor-icons/react';

import { fetchAPI } from '@/api/fetch';
import { Job } from '@/types/Job';

import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import {
  EmploymentType,
  getEmploymentTypeLabel,
  getEmploymentTypeOptions,
  getWorkTypeLabel,
  getWorkTypeOptions,
  WorkType
} from '@/types/enums';
import {
  AnalyzeJobRequirementsResponse,
  apiAnalyzeJobRequirements,
  apiExtractJD,
  apiGetCurrencies,
  apiGetJobCategories,
  apiUpdateJob,
  GetJobCategoriesResponse
} from '@/api/services';
import { JobDescription } from '@/types/JobDescription';
import RichEditor from '@/components/forms/RichEditor';
import { RequiredFieldLabel } from '@/components/forms/RequiredFieldLabel';
import { MAX_RESUME_FILE_SIZE, MAX_RESUME_FILE_SIZE_MB } from '@/config/constants';

export default function EditJobDialog({
  job,
  onUpdateJob,
  disabled = false
}: {
  job: Job;
  onUpdateJob: (job: Job) => void;
  disabled?: boolean;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [requirements, setRequirements] = useState<string[]>(job.requirements?.requirements || []);
  const [niceToHaves, setNiceToHaves] = useState<string[]>(job.requirements?.nice_to_haves || []);
  const [positionTags, setPositionTags] = useState<string[]>(job.position_tags || []);
  const [submitError, setSubmitError] = useState<boolean>(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState<string>('');
  const [loadingText, setLoadingText] = useState('');
  const fileSizeLimit = MAX_RESUME_FILE_SIZE;

  useEffect(() => {
    if (!isFileUploading) return;

    const baseMessages = [
      'Uploading',
      'Processing',
      'Extracting',
      'Analyzing',
      'Summarizing',
      'Processing',
      'Analyzing',
      'Summarizing'
    ];
    let index = 0;
    let dotCount = 0;
    const maxDots = 3;
    const maxRepeats = 5;
    let repeatCount = 0;

    const updateLoadingText = () => {
      setLoadingText(`${baseMessages[index]}${'.'.repeat(dotCount)}`);
      dotCount = (dotCount + 1) % (maxDots + 1);
      if (dotCount === 0 && ++repeatCount === maxRepeats) {
        index = (index + 1) % baseMessages.length;
        repeatCount = 0;
      }
    };

    const intervalId = setInterval(updateLoadingText, 500);
    updateLoadingText();

    return () => clearInterval(intervalId);
  }, [isFileUploading]);

  const formatCurrency = (amount: number) => `$ ${Number(amount).toLocaleString()}`;

  interface FormValues {
    jobTitle: string;
    category: string;
    currency: { value: Currency };
    salaryFrom: string;
    salaryTo: string;
    employmentType?: { value: EmploymentType };
    workType?: { value: WorkType };
    location: string;
    jobDescription: string;
    clientContactName: string;
    clientContactRole: string;
    clientContactEmail: string;
    clientContactPhone: string;
  }

  const {
    handleSubmit,
    register,
    reset,
    trigger,
    setValue,
    control,
    watch,
    setError,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    defaultValues: {
      jobTitle: job.job_title,
      category: job.category,
      salaryFrom: job.salary_from ? formatCurrency(job.salary_from) : '',
      salaryTo: job.salary_to ? formatCurrency(job.salary_to) : '',
      employmentType: { value: job.employment_type },
      workType: { value: job.work_type },
      location: job.location,
      jobDescription: job.description,
      clientContactName: job.contact_name,
      clientContactRole: job.contact_role,
      clientContactEmail: job.contact_email,
      clientContactPhone: job.contact_phone
    }
  });

  useEffect(() => {
    setValue('jobTitle', job.job_title);
    setValue('category', job.category);
    setValue('salaryFrom', job.salary_from ? formatCurrency(job.salary_from) : '');
    setValue('salaryTo', job.salary_to ? formatCurrency(job.salary_to) : '');
    setValue('employmentType', { value: job.employment_type });
    setValue('workType', { value: job.work_type });
    setValue('location', job.location);
    setValue('jobDescription', job.description);
    setValue('clientContactName', job.contact_name);
    setValue('clientContactRole', job.contact_role);
    setValue('clientContactEmail', job.contact_email);
    setValue('clientContactPhone', job.contact_phone);

    setRequirements(job.requirements?.requirements || []);
    setNiceToHaves(job.requirements?.nice_to_haves || []);
    setPositionTags(job.position_tags || []);

    if (isDialogOpen) {
      const currency = currencies.find((currency) => currency.code === job.currency);
      if (currency) {
        setValue('currency', { value: currency });
      }
    }
  }, [job, isDialogOpen, currencies]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await fetchAPI<Currency[]>({
          request: apiGetCurrencies
        });

        if (response.success) {
          const currenciesData = response.data;
          setCurrencies(currenciesData);

          const currency = currenciesData.find((currency) => currency.code === job.currency);
          if (currency) {
            setValue('currency', { value: currency });
          }
        } else {
          console.error('Error fetching currencies:', response.message);
        }
      } catch (error) {
        console.error('Unexpected error fetching currencies:', error);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await fetchAPI<GetJobCategoriesResponse>({
          request: () => apiGetJobCategories()
        });

        if (response.success && response.data) {
          setCategories(response.data.categories);
        } else {
          console.error('Error fetching categories:', response.message);
        }
      } catch (error) {
        console.error('Unexpected error fetching categories:', error);
      }
    };

    if (isDialogOpen) {
      fetchCurrencies();
      fetchCategories();
    }
  }, [isDialogOpen]);

  const parseSalary = (salary: string) => Number(salary.replace(/[$,]/g, ''));

  const handleAddNewCategory = (onChange?: (value: string) => void) => {
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      const updatedCategories = [...categories, newCategory.trim()];
      setCategories(updatedCategories);
      if (onChange) {
        onChange(newCategory.trim());
      } else {
        setValue('category', newCategory.trim());
      }
      setNewCategory('');
    }
  };

  const onSubmit = async (formData: FormValues) => {
    setSubmitError(false);

    const isSalaryFromEmpty = !formData.salaryFrom;
    const isSalaryToEmpty = !formData.salaryTo;

    if (isSalaryFromEmpty !== isSalaryToEmpty) {
      const errorMessage = 'Both salary fields must be filled together';
      setError('salaryFrom', { message: errorMessage });
      setError('salaryTo', { message: errorMessage });
      return;
    }

    const salaryFromValue = formData.salaryFrom ? parseSalary(formData.salaryFrom) : null;
    const salaryToValue = formData.salaryTo ? parseSalary(formData.salaryTo) : null;

    const isSalaryFromInvalid = salaryFromValue !== null && salaryFromValue <= 0;
    const isSalaryToInvalid = salaryToValue !== null && salaryToValue <= 0;

    if (isSalaryFromInvalid || isSalaryToInvalid) {
      if (isSalaryFromInvalid) {
        setValue('salaryFrom', '');
        await trigger('salaryFrom');
      }
      if (isSalaryToInvalid) {
        setValue('salaryTo', '');
        await trigger('salaryTo');
      }
      return;
    }

    try {
      const response = await fetchAPI<void>({
        request: () =>
          apiUpdateJob({
            jobId: job.job_id,
            payload: {
              job_title: formData.jobTitle,
              category: formData.category,
              currency: formData.currency.value.code,
              salary_from: salaryFromValue,
              salary_to: salaryToValue,
              employment_type: formData.employmentType!.value,
              work_type: formData.workType!.value,
              location: formData.location,
              job_description: formData.jobDescription,
              contact_name: formData.clientContactName,
              contact_role: formData.clientContactRole,
              contact_email: formData.clientContactEmail,
              contact_phone: formData.clientContactPhone,
              requirements: {
                requirements,
                nice_to_haves: niceToHaves
              },
              position_tags: positionTags
            }
          })
      });

      if (response.success) {
        onUpdateJob({
          ...job,
          job_title: formData.jobTitle,
          category: formData.category,
          currency: formData.currency.value.code,
          salary_from: salaryFromValue,
          salary_to: salaryToValue,
          employment_type: formData.employmentType!.value,
          work_type: formData.workType!.value,
          location: formData.location,
          description: formData.jobDescription,
          contact_name: formData.clientContactName,
          contact_role: formData.clientContactRole,
          contact_email: formData.clientContactEmail,
          contact_phone: formData.clientContactPhone,
          requirements: {
            requirements,
            nice_to_haves: niceToHaves
          }
        });

        setIsDialogOpen(false);
      } else {
        console.error('Failed to update job:', response.message);
        setSubmitError(true);
      }
    } catch (error) {
      console.error('Error submitting data:', error);
      setSubmitError(true);
    }
  };

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    setUploadError(null);
    handleSubmit(onSubmit)();
  };

  const handleDialogClose = () => {
    reset();
    setUploadedFile(null);
    setUploadError(null);
    setRequirements(job.requirements?.requirements || []);
    setNiceToHaves(job.requirements?.nice_to_haves || []);
    setPositionTags(job.position_tags || []);
  };

  const onUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.size > fileSizeLimit) {
      setUploadError(`File size exceeds ${MAX_RESUME_FILE_SIZE_MB} MB`);
      return;
    }

    setUploadedFile(selectedFile);
    setIsFileUploading(true);
    setUploadError(null);

    try {
      const response = await fetchAPI<JobDescription>({
        request: () =>
          apiExtractJD({
            payload: { file: selectedFile }
          })
      });

      if (response.success) {
        const {
          job_title,
          employment_type,
          work_type,
          location,
          job_description,
          salary_currency,
          salary_from,
          salary_to,
          contact_name,
          contact_role,
          contact_email,
          contact_phone
        } = response.data;

        const jobRequirementsResponse = await fetchAPI<AnalyzeJobRequirementsResponse>({
          request: () =>
            apiAnalyzeJobRequirements({
              payload: {
                job_title: job_title,
                job_description: job_description
              }
            })
        });

        if (jobRequirementsResponse.success) {
          const { requirements, nice_to_haves, position_tags } = jobRequirementsResponse.data;
          setRequirements(requirements);
          setNiceToHaves(nice_to_haves);
          setPositionTags(position_tags);
        } else {
          console.error('Error extracting job requirements:', jobRequirementsResponse.message);
          setUploadError('Failed to extract job requirements. Please try again.');
        }

        setValue('jobTitle', job_title);
        setValue('salaryFrom', salary_from ? formatCurrency(salary_from) : '');
        setValue('salaryTo', salary_to ? formatCurrency(salary_to) : '');
        setValue('employmentType', { value: employment_type });
        setValue('workType', { value: work_type });
        setValue('location', location);
        setValue('jobDescription', job_description);
        setValue('clientContactName', contact_name);
        setValue('clientContactRole', contact_role);
        setValue('clientContactEmail', contact_email);
        setValue('clientContactPhone', contact_phone);

        const currency = currencies.find((c) => c.code === salary_currency);
        if (currency) {
          setValue('currency', { value: currency });
        }
      } else {
        console.error('Error extracting job description:', response.message);
        setUploadError('Failed to extract job description. Please try again.');
      }
    } catch (error) {
      console.error('Error extracting job description:', error);
      setUploadError('Failed to extract job description. Please try again.');
    } finally {
      setIsFileUploading(false);
    }
  };

  const handleClearFile = () => {
    setUploadedFile(null);
    setUploadError(null);
    setIsFileUploading(false);
    reset();
  };

  const getFileSize = (size?: number) => {
    if (!size) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <DialogRoot
      size="xl"
      open={isDialogOpen}
      onOpenChange={(e) => setIsDialogOpen(e.open)}
      closeOnInteractOutside={false}
      placement="center"
      onExitComplete={handleDialogClose}
      unmountOnExit
    >
      <DialogTrigger asChild>
        <Button
          bg="background.secondary"
          border="1px solid"
          borderColor="primary.900"
          borderRadius="8px"
          color="primary.900"
          fontSize="14px"
          disabled={disabled}
        >
          Edit <PencilSimpleLine size={20} color="#4E76FF" />
        </Button>
      </DialogTrigger>
      <DialogContent borderRadius="12px" style={{ zIndex: 1000 }}>
        <form onSubmit={handleFormSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Job Details</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap="4" mb="24px">
              <Box as="hr" borderColor="neutrals.100" my="4" />
              <Box>
                <Box color="neutrals.800" fontSize="18px">
                  Upload a job description file, and we'll fill in the details for you.
                </Box>
                <Box color="neutrals.500" fontSize="16px">
                  Upload PDF, or TXT file types ({MAX_RESUME_FILE_SIZE_MB}MB max)
                </Box>
              </Box>
              {uploadedFile || isFileUploading ? (
                <Box display="flex" alignItems="center" width="100%">
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    width="150px"
                    height="150px"
                    borderRadius="12px"
                    border="1px solid"
                    borderColor="primary.100"
                    bg="primary.50"
                  >
                    {isFileUploading ? (
                      <>
                        <Spinner size="lg" borderWidth="4px" color="primary.900" />
                        <Box
                          mt="8px"
                          fontSize="16px"
                          fontWeight="semibold"
                          color="neutrals.900"
                          textAlign="center"
                        >
                          {loadingText}
                        </Box>
                      </>
                    ) : (
                      <File size={64} color="#4e76ff" />
                    )}
                  </Box>
                  <Box ml="32px" color="neutrals.900">
                    <Box display="flex" alignItems="center">
                      {!isFileUploading && (
                        <Box mr="12px">
                          <CheckCircle size={24} color="#4e76ff" weight="fill" />
                        </Box>
                      )}
                      <Box fontSize="18px" color="neutrals.800" mr="16px">
                        {uploadedFile?.name}
                      </Box>
                      {!isFileUploading && (
                        <X size={16} color="#545454" onClick={handleClearFile} cursor="pointer" />
                      )}
                    </Box>
                    <Box h="4px" />
                    <Box fontSize="16px" color="neutrals.500">
                      {getFileSize(uploadedFile?.size)}
                    </Box>
                    {uploadError && (
                      <Box fontSize="16px" color="error.700" mt="4px">
                        {uploadError}
                      </Box>
                    )}
                  </Box>
                </Box>
              ) : (
                <Stack>
                  <Box
                    w="100%"
                    h="200px"
                    border="1px dashed"
                    borderColor={uploadError ? 'error.700' : 'neutrals.300'}
                    borderRadius="12px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    bg="neutrals.50"
                    position="relative"
                  >
                    <input
                      id="resumeFile"
                      type="file"
                      accept=".pdf,.txt"
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                      onChange={onUploadFile}
                    />
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <UploadSimple size={60} color="#404040" />
                      <Box textAlign="center" color="neutrals.900" fontSize="14px">
                        Drop file here or{' '}
                        <Box as="span" color="primary.900">
                          click to upload
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  {uploadError && (
                    <Box fontSize="14px" color="error.700">
                      {uploadError}
                    </Box>
                  )}
                </Stack>
              )}

              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    Job Title
                  </Box>
                }
                invalid={!!errors.jobTitle}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.jobTitle?.message}
                  </Box>
                }
              >
                <Input
                  id="candidateName"
                  size="lg"
                  placeholder="Enter name"
                  disabled={isFileUploading || isSubmitting}
                  _disabled={{
                    bg: 'neutrals.300'
                  }}
                  {...register('jobTitle', {
                    required: 'Job title is required'
                  })}
                />
              </Field>
              <Field
                label={<RequiredFieldLabel>Category</RequiredFieldLabel>}
                invalid={!!errors.category}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.category?.message}
                  </Box>
                }
              >
                <Controller
                  name="category"
                  control={control}
                  rules={{ required: 'Category is required' }}
                  render={({ field }) => (
                    <Menu.Root positioning={{ placement: 'bottom-start' }}>
                      <Menu.Trigger asChild>
                        <Button
                          variant="outline"
                          size="lg"
                          border="1px solid"
                          borderRadius="8px"
                          borderColor={errors.category ? 'error.500' : 'neutrals.200'}
                          w="100%"
                          textAlign="left"
                          fontSize="16px"
                          disabled={isFileUploading || isSubmitting}
                          _disabled={{
                            bg: 'neutrals.300'
                          }}
                        >
                          <Flex
                            justify="space-between"
                            align="center"
                            w="100%"
                            color={field.value ? 'neutrals.900' : 'neutrals.500'}
                          >
                            {field.value || 'Select a category'}
                            <CaretDown size={16} />
                          </Flex>
                        </Button>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content borderRadius="8px" style={{ zIndex: 1500 }}>
                          {categories.length > 0 && (
                            <>
                              {categories.map((category) => (
                                <Menu.Item
                                  key={category}
                                  value={category}
                                  cursor="pointer"
                                  fontSize="16px"
                                  _hover={{
                                    cursor: 'pointer',
                                    bg: 'neutrals.100'
                                  }}
                                  onClick={() => {
                                    field.onChange(category);
                                  }}
                                >
                                  {category}
                                </Menu.Item>
                              ))}
                              <Menu.Separator />
                            </>
                          )}
                          <Menu.Item
                            value="new-category-input"
                            closeOnSelect={false}
                            _hover={{ bg: 'transparent' }}
                          >
                            <HStack w="100%" onClick={(e) => e.stopPropagation()}>
                              <Input
                                fontSize="16px"
                                placeholder="Enter new category"
                                value={newCategory}
                                size="sm"
                                onChange={(e) => setNewCategory(e.target.value)}
                                onKeyDown={(e) => {
                                  e.stopPropagation();
                                  if (e.key === 'Enter') {
                                    handleAddNewCategory(field.onChange);
                                  }
                                }}
                              />
                              <IconButton
                                variant="ghost"
                                size="sm"
                                color="primary.900"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddNewCategory(field.onChange);
                                }}
                                disabled={!newCategory.trim()}
                              >
                                <Plus size={16} weight="bold" />
                              </IconButton>
                            </HStack>
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  )}
                />
              </Field>
              <Flex gap="10px" alignItems="flex-start" w="100%">
                <Box flex="1">
                  <Field
                    label={<Box fontSize="16px">Salary</Box>}
                    invalid={!!errors.salaryFrom || !!errors.salaryTo}
                    errorText={
                      <Box fontSize="14px" color="error.700">
                        {errors.salaryFrom?.message || errors.salaryTo?.message}
                      </Box>
                    }
                  >
                    <Stack direction="row" width="100%" gap="8px">
                      {['salaryFrom', 'salaryTo'].map((field, index) => (
                        <Group key={field} attached width="100%">
                          <InputAddon fontSize="16px" bg="background.secondary">
                            {index === 0 ? 'From' : 'To'}
                          </InputAddon>
                          <Input
                            disabled={isFileUploading || isSubmitting}
                            size="lg"
                            placeholder={index === 0 ? '$50,000' : '$100,000'}
                            width="100%"
                            type="text"
                            borderRadius="8px"
                            _disabled={{
                              bg: 'neutrals.300'
                            }}
                            {...register(field as 'salaryFrom' | 'salaryTo')}
                            onChange={(e) => {
                              const inputValue = e.target.value.trim();
                              if (!inputValue || inputValue === '$') {
                                e.target.value = '';
                                return;
                              }

                              let formattedValue = inputValue.startsWith('$')
                                ? inputValue.slice(1)
                                : inputValue;
                              const lastCharacter = formattedValue.slice(-1);

                              if (isNaN(Number(lastCharacter))) {
                                formattedValue = formattedValue.slice(0, -1);
                              }

                              if (formattedValue) {
                                const numericValue = Number(formattedValue.replace(/,/g, ''));
                                e.target.value = '$ ' + numericValue.toLocaleString();
                              } else {
                                e.target.value = '';
                              }
                            }}
                          />
                        </Group>
                      ))}
                    </Stack>
                  </Field>
                </Box>
                <Box w="280px">
                  <Field
                    label={<Box fontSize="16px">Salary Currency</Box>}
                    invalid={!!errors.currency}
                    errorText={
                      <Box fontSize="14px" color="error.700">
                        {errors.currency?.message}
                      </Box>
                    }
                  >
                    <Controller
                      name="currency"
                      control={control}
                      rules={{ required: 'Currency is required' }}
                      render={({ field }) => (
                        <Menu.Root>
                          <Menu.Trigger asChild>
                            <Button
                              variant="outline"
                              size="lg"
                              borderColor={errors.currency ? 'error.500' : 'neutrals.200'}
                              borderRadius="8px"
                              disabled={isFileUploading || isSubmitting}
                              _disabled={{
                                bg: 'neutrals.300'
                              }}
                              w="280px"
                            >
                              <Flex justifyContent="space-between" alignItems="center" width="100%">
                                <Box
                                  fontSize="16px"
                                  color="neutrals.700"
                                  overflow="hidden"
                                  whiteSpace="nowrap"
                                  textOverflow="ellipsis"
                                >
                                  {field.value?.value
                                    ? `${field.value.value.code} - ${field.value.value.name}`
                                    : 'Select currency'}
                                </Box>
                                <Box as="span" ml="8px" mr="0">
                                  <CaretDown size={16} />
                                </Box>
                              </Flex>
                            </Button>
                          </Menu.Trigger>
                          <Menu.Positioner>
                            <Menu.Content minW="10rem" style={{ zIndex: 1500 }}>
                              <Menu.RadioItemGroup
                                value={field.value?.value.code || ''}
                                onValueChange={(value) => {
                                  const selectedCurrency = currencies.find(
                                    (item) => item.code === value.value
                                  );
                                  field.onChange(
                                    selectedCurrency ? { value: selectedCurrency } : null
                                  );
                                }}
                              >
                                {currencies.map(({ code, name }) => (
                                  <Menu.RadioItem
                                    key={code}
                                    value={code}
                                    fontSize="16px"
                                    _hover={{
                                      cursor: 'pointer',
                                      bg: 'neutrals.100'
                                    }}
                                  >
                                    {`${code} - ${name}`}
                                  </Menu.RadioItem>
                                ))}
                              </Menu.RadioItemGroup>
                            </Menu.Content>
                          </Menu.Positioner>
                        </Menu.Root>
                      )}
                    />
                  </Field>
                </Box>
              </Flex>
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    Employment Type
                  </Box>
                }
                invalid={!!errors.employmentType}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.employmentType?.message}
                  </Box>
                }
              >
                <Controller
                  name="employmentType"
                  control={control}
                  rules={{ required: 'Employment type is required' }}
                  render={({ field }) => (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          variant="outline"
                          size="lg"
                          width="100%"
                          borderRadius="8px"
                          borderColor={errors.employmentType ? 'error.500' : 'neutrals.200'}
                          disabled={isFileUploading || isSubmitting}
                          _disabled={{
                            bg: 'neutrals.300'
                          }}
                        >
                          <Flex justifyContent="space-between" alignItems="center" width="100%">
                            <Box fontSize="16px" color="neutrals.700">
                              {field.value
                                ? getEmploymentTypeLabel(field.value.value)
                                : 'Select employment type'}
                            </Box>
                            <Box as="span" ml="auto" mr="0">
                              <CaretDown size={16} />
                            </Box>
                          </Flex>
                        </Button>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content minW="10rem" style={{ zIndex: 1500 }}>
                          <Menu.RadioItemGroup
                            value={field.value ? field.value.value : ''}
                            onValueChange={field.onChange}
                          >
                            {getEmploymentTypeOptions().map((item) => (
                              <Menu.RadioItem
                                key={item.value}
                                value={item.value}
                                fontSize="16px"
                                _hover={{
                                  cursor: 'pointer',
                                  bg: 'neutrals.100'
                                }}
                              >
                                {item.label}
                              </Menu.RadioItem>
                            ))}
                          </Menu.RadioItemGroup>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  )}
                />
              </Field>
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    Work Type
                  </Box>
                }
                invalid={!!errors.workType}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.workType?.message}
                  </Box>
                }
              >
                <Controller
                  name="workType"
                  control={control}
                  rules={{ required: 'Work type is required' }}
                  render={({ field }) => (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          variant="outline"
                          size="lg"
                          width="100%"
                          borderRadius="8px"
                          borderColor={errors.workType ? 'error.500' : 'neutrals.200'}
                          disabled={isFileUploading || isSubmitting}
                          _disabled={{
                            bg: 'neutrals.300'
                          }}
                        >
                          <Flex justifyContent="space-between" alignItems="center" width="100%">
                            <Box fontSize="16px" color="neutrals.700">
                              {field.value
                                ? getWorkTypeLabel(field.value.value)
                                : 'Select work type'}
                            </Box>
                            <Box as="span" ml="auto" mr="0">
                              <CaretDown size={16} />
                            </Box>
                          </Flex>
                        </Button>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content minW="10rem" style={{ zIndex: 1500 }}>
                          <Menu.RadioItemGroup
                            value={field.value ? field.value.value : ''}
                            onValueChange={field.onChange}
                          >
                            {getWorkTypeOptions().map((item) => (
                              <Menu.RadioItem
                                key={item.value}
                                value={item.value}
                                fontSize="16px"
                                _hover={{
                                  cursor: 'pointer',
                                  bg: 'neutrals.100'
                                }}
                              >
                                {item.label}
                              </Menu.RadioItem>
                            ))}
                          </Menu.RadioItemGroup>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  )}
                />
              </Field>
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    Location
                  </Box>
                }
                invalid={!!errors.location}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.location?.message}
                  </Box>
                }
              >
                <Input
                  disabled={isFileUploading || isSubmitting}
                  _disabled={{
                    bg: 'neutrals.300'
                  }}
                  size="lg"
                  borderRadius="8px"
                  placeholder="Enter location"
                  {...register('location', {
                    required: 'Location is required'
                  })}
                />
              </Field>
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    Job Description
                  </Box>
                }
                invalid={!!errors.jobDescription}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.jobDescription?.message}
                  </Box>
                }
              >
                <Box
                  w="100%"
                  h="100%"
                  border="1px solid"
                  borderColor="neutrals.200"
                  borderRadius="8px"
                  p="16px 24px"
                  bg={isFileUploading || isSubmitting ? 'neutrals.100' : 'transparent'}
                >
                  <RichEditor
                    key="job-description"
                    content={watch('jobDescription')}
                    type="job-description"
                    placeholder="Briefly describe the role, including key responsibilities, required qualifications, and any other relevant details (e.g., job expectations, benefits, or work conditions)."
                    onChange={(content) => {
                      setValue('jobDescription', content);
                    }}
                    readOnly={isFileUploading || isSubmitting}
                    className={isFileUploading || isSubmitting ? 'read-only' : ''}
                  />
                </Box>
              </Field>
            </Stack>
            <Box fontWeight="semibold" color="neutrals.800" fontSize="18px" mb="24px">
              Client Contact
            </Box>
            <Stack gap="16px" align="flex-start" width="100%" mb="24px">
              <Stack direction="row" width="100%" gap="8px">
                <Field
                  label={<Box fontSize="16px">Name</Box>}
                  invalid={!!errors.clientContactName}
                  errorText={
                    <Box fontSize="14px" color="error.700">
                      {errors.clientContactName?.message}
                    </Box>
                  }
                >
                  <Input
                    disabled={isFileUploading || isSubmitting}
                    _disabled={{
                      bg: 'neutrals.300'
                    }}
                    size="lg"
                    borderRadius="8px"
                    placeholder="Enter client name"
                    {...register('clientContactName')}
                  />
                </Field>
                <Field
                  label={<Box fontSize="16px">Role</Box>}
                  invalid={!!errors.clientContactRole}
                  errorText={
                    <Box fontSize="14px" color="error.700">
                      {errors.clientContactRole?.message}
                    </Box>
                  }
                >
                  <Input
                    disabled={isFileUploading || isSubmitting}
                    _disabled={{
                      bg: 'neutrals.300'
                    }}
                    size="lg"
                    borderRadius="8px"
                    placeholder="Enter role"
                    {...register('clientContactRole')}
                  />
                </Field>
              </Stack>
              <Stack direction="row" width="100%" gap="8px">
                <Field
                  label={<Box fontSize="16px">Email</Box>}
                  invalid={!!errors.clientContactEmail}
                  errorText={
                    <Box fontSize="14px" color="error.700">
                      {errors.clientContactEmail?.message}
                    </Box>
                  }
                >
                  <Input
                    disabled={isFileUploading || isSubmitting}
                    _disabled={{
                      bg: 'neutrals.300'
                    }}
                    size="lg"
                    borderRadius="8px"
                    placeholder="Enter email"
                    {...register('clientContactEmail', {
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: 'Invalid email address'
                      }
                    })}
                  />
                </Field>
                <Field
                  label={<Box fontSize="16px">Phone</Box>}
                  invalid={!!errors.clientContactPhone}
                  errorText={
                    <Box fontSize="14px" color="error.700">
                      {errors.clientContactPhone?.message}
                    </Box>
                  }
                >
                  <Input
                    disabled={isFileUploading || isSubmitting}
                    _disabled={{
                      bg: 'neutrals.300'
                    }}
                    size="lg"
                    borderRadius="8px"
                    placeholder="Enter phone"
                    {...register('clientContactPhone')}
                  />
                </Field>
              </Stack>
            </Stack>
            <Box fontWeight="semibold" color="neutrals.800" fontSize="18px" mb="24px">
              AI Analysis
            </Box>
            {['Requirements', 'Nice To Have'].map((section, sectionIndex) => (
              <Box
                key={sectionIndex}
                borderRadius="12px"
                border="1px solid"
                borderColor="neutrals.300"
                width="80%"
                mb="20px"
              >
                <Stack direction="column">
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    bg="neutrals.50"
                    p="16px 32px"
                    borderBottom="1px solid"
                    borderColor="neutrals.300"
                    borderTopRadius="12px"
                  >
                    <Box fontWeight="bold" fontSize="18px">
                      {section}
                    </Box>
                    <Button
                      size="sm"
                      bg="background.secondary"
                      borderColor="primary.900"
                      borderRadius="8px"
                      color="primary.900"
                      fontSize="14px"
                      disabled={isFileUploading || isSubmitting}
                      onClick={() =>
                        section === 'Requirements'
                          ? setRequirements([...requirements, ''])
                          : setNiceToHaves([...niceToHaves, ''])
                      }
                    >
                      <Plus size={24} />
                      Add New
                    </Button>
                  </Flex>
                  {(section === 'Requirements' ? requirements : niceToHaves).map((item, index) => (
                    <Flex
                      key={index}
                      justifyContent="space-between"
                      alignItems="center"
                      p="10px 32px 16px 16px"
                      borderBottom={
                        index !==
                        (section === 'Requirements' ? requirements.length : niceToHaves.length) - 1
                          ? '1px solid'
                          : 'none'
                      }
                      borderColor="neutrals.300"
                    >
                      <Input
                        w="80%"
                        ml={2}
                        disabled={isFileUploading || isSubmitting}
                        _disabled={{
                          bg: 'neutrals.300'
                        }}
                        value={item}
                        border="1px solid"
                        borderColor="neutrals.300"
                        borderRadius="8px"
                        bg="transparent"
                        fontSize="16px"
                        color="inherit"
                        onChange={(e) =>
                          section === 'Requirements'
                            ? setRequirements([
                                ...requirements.slice(0, index),
                                e.target.value,
                                ...requirements.slice(index + 1)
                              ])
                            : setNiceToHaves([
                                ...niceToHaves.slice(0, index),
                                e.target.value,
                                ...niceToHaves.slice(index + 1)
                              ])
                        }
                      />
                      <Trash
                        size={20}
                        cursor={isFileUploading || isSubmitting ? 'not-allowed' : 'pointer'}
                        onClick={() => {
                          if (isFileUploading || isSubmitting) return;
                          const updateList =
                            section === 'Requirements' ? setRequirements : setNiceToHaves;
                          const currentList =
                            section === 'Requirements' ? requirements : niceToHaves;
                          updateList(currentList.filter((_, i) => i !== index));
                        }}
                      />
                    </Flex>
                  ))}
                </Stack>
              </Box>
            ))}
            {submitError && (
              <Box fontSize="16px" color="error.700" mb="16px">
                Failed to update job. Please try again.
              </Box>
            )}
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button
                w="120px"
                borderColor="primary.900"
                background="background.secondary"
                color="primary.900"
                borderRadius="8px"
                fontSize="16px"
                disabled={isSubmitting || isFileUploading}
              >
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              type="submit"
              w="120px"
              background="primary.900"
              color="neutrals.0"
              borderRadius="8px"
              fontSize="16px"
              disabled={isFileUploading}
              loading={isSubmitting}
            >
              Save
            </Button>
          </DialogFooter>
        </form>
        <DialogCloseTrigger m="12px" disabled={isSubmitting || isFileUploading}>
          <X size={32} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
